import { useMemo } from "react";
import type { FC } from "react";

import type {
  AvailablePromotion,
  PreviewPromotion,
  TranslationData,
  VariantItemPdp,
} from "@/common/lib/types";
import { giftTypes, promotionType } from "@/apiUtils/constants";
import { isFuturePromotionPreviewPricing } from "./promotionPreviewUtils";

interface PromotionPreviewNoticeProps {
  language: string;
  pricing?: VariantItemPdp["pricing"];
  translations?: TranslationData;
  availablePromotions?: AvailablePromotion[] | null;
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const formatRewardValue = (value?: number | string | null) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return `${value}`;
  }

  return Number.isInteger(numericValue) ? `${numericValue}` : `${Number(numericValue.toFixed(2))}`;
};

const getDaysUntilStart = (startDate?: string | null) => {
  if (!startDate) {
    return null;
  }

  const startTime = new Date(startDate).getTime();

  if (Number.isNaN(startTime)) {
    return null;
  }

  return Math.max(0, Math.ceil((startTime - Date.now()) / DAY_IN_MS));
};

const applyTemplate = (template: string, values: Record<string, string | number>) => {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => `${values[key] ?? ""}`);
};

const buildSaleorDiscountAmount = (
  rewardValue: string,
  rewardType: string,
  currency: string,
): string => {
  if (!rewardValue) {
    return "";
  }

  if (rewardType === "PERCENTAGE") {
    return `${rewardValue}%`;
  }

  return `${currency} ${rewardValue}`.trim();
};

/** Remove VIP extra segment when backend sends no previewAdditionalDiscountPercent. */
const stripExtraSegment = (text: string) =>
  text.replace(/\s*\+\s*an extra\s*[\d.]+%?\s*/i, "").replace(/\s{2,}/g, " ").trim();

const findMatchedAvailablePromotion = (
  previewPromotion?: PreviewPromotion | null,
  availablePromotions?: AvailablePromotion[] | null,
) => {
  if (!previewPromotion) {
    return undefined;
  }

  return (availablePromotions ?? []).find(
    (promo) =>
      promo?.promotion?.name === previewPromotion.name ||
      (promo?.promotion?.startDate &&
        promo.promotion.startDate === previewPromotion.startDate),
  );
};

const isBxgyPreviewPromotion = (
  previewPromotion?: PreviewPromotion | null,
  availablePromotions?: AvailablePromotion[] | null,
) => {
  const matched = findMatchedAvailablePromotion(previewPromotion, availablePromotions);

  return (
    matched?.promotion?.type === promotionType.BUYXGETY ||
    matched?.giftType === giftTypes.FREE_GIFT
  );
};

/**
 * VIP early-access green banner only (no product-offer badge).
 * Templates: BXGY vs catalogue/ORDER — both DoctorM Sanity keys.
 * ORDER-level VIP must use the catalogue/order template and may only send extra %.
 */
const PromotionPreviewNotice: FC<PromotionPreviewNoticeProps> = ({
  language,
  pricing,
  translations,
  availablePromotions,
}) => {
  const previewPromotion = pricing?.previewPromotion;
  const showPreviewNotice = isFuturePromotionPreviewPricing(pricing);

  const displayText = useMemo(() => {
    if (!showPreviewNotice) {
      return "";
    }

    const promotionName = previewPromotion?.name || "";
    const rewardValue = formatRewardValue(previewPromotion?.rewardValue);
    const rewardType = `${previewPromotion?.rewardValueType || ""}`.toUpperCase();
    const currency = pricing?.price?.currency || pricing?.priceUndiscounted?.currency || "";
    const extraPercent = formatRewardValue(previewPromotion?.previewAdditionalDiscountPercent);
    const daysUntilStart = getDaysUntilStart(previewPromotion?.startDate);
    const pdpTranslations = translations?.pdp ?? {};
    const isBxgy = isBxgyPreviewPromotion(previewPromotion, availablePromotions);

    // Catalogue + ORDER share the same early-access template (not the BXGY one).
    const template = isBxgy
      ? pdpTranslations.pdp_promotion_preview_bxgy_template
      : pdpTranslations.pdp_promotion_preview_catalog_order_template;

    if (!template || daysUntilStart === null || !promotionName) {
      return "";
    }

    let discount = buildSaleorDiscountAmount(rewardValue, rewardType, currency);
    // Pure ORDER VIP preview may only send previewAdditionalDiscountPercent — still show banner.
    const usedExtraAsPrimaryDiscount = !discount && !!extraPercent;
    if (usedExtraAsPrimaryDiscount) {
      discount = `${extraPercent}%`;
    }

    if (!isBxgy && !discount) {
      return "";
    }

    let text = applyTemplate(template, {
      name: promotionName,
      discount,
      extra: usedExtraAsPrimaryDiscount ? "" : extraPercent,
      days: daysUntilStart,
      currency,
      value: rewardValue || extraPercent,
    }).trim();

    if (!extraPercent || usedExtraAsPrimaryDiscount) {
      text = stripExtraSegment(text);
    }

    return text;
  }, [
    availablePromotions,
    language,
    previewPromotion,
    pricing?.price?.currency,
    pricing?.priceUndiscounted?.currency,
    showPreviewNotice,
    translations,
  ]);

  if (!showPreviewNotice || !displayText) {
    return null;
  }

  return (
    <div className="mb-4 flex w-full items-center justify-center gap-2 bg-[#edf9ea] px-4 py-2.5 text-black">
      <span aria-hidden="true" className="flex size-6 shrink-0 items-center justify-center text-black">
        <svg width="22" height="22" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M20 3.5L23.7 7.3L29 6.7L30.2 11.9L35 14.2L32.9 19.1L35 24L30.2 26.3L29 31.5L23.7 30.9L20 34.7L16.3 30.9L11 31.5L9.8 26.3L5 24L7.1 19.1L5 14.2L9.8 11.9L11 6.7L16.3 7.3L20 3.5Z"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <path d="M15 25L25 15" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          <path d="M15.5 16.5H15.52" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
          <path d="M24.5 23.5H24.52" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
        </svg>
      </span>
      <div className="text-center text-xs font-bold">{displayText}</div>
    </div>
  );
};

export default PromotionPreviewNotice;
